import React, { useState, useEffect, useMemo } from "react";
import Layout, { Container } from "../components/layout";
import axios from "axios";
import { CheckCircle, Truck, XCircle, Package } from "lucide-react";
import { apiurl } from "../config/config";
import {SuwasthiLoader} from "../components/loader.js"
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

const ShipmentAnalytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
  });

  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];

    setFilters({
      startDate: today,
      endDate: today,
    });

    fetchData(today, today);
  }, []);

  const fetchData = async (start, end) => {
    setLoading(true);
    try {
      if (!start || !end) return;

      const res = await axios.get(`${apiurl}/api/shipment/analytics`, {
        params: {
          startDate: start,
          endDate: end,
        },
      });

      setData(res.data);
          setLoading(false);
    } catch (err) {
          setLoading(false);
      console.error("API ERROR:", err);
    }
  };

  // 🎯 CLEAN ATTEMPTS (REMOVE 0)
  const cleanAttempts = useMemo(() => {
    const obj = { ...data?.attemptStatusBreakdown };
    delete obj?.["0"];
    return obj || {};
  }, [data]);

  // 📊 GRAPH
  const graphData = useMemo(() => {
    return Object.entries(cleanAttempts).map(([k, v]) => {
      const delivered = v?.DELIVERED || 0;
      const ofd = v?.OUT_FOR_DELIVERY || 0;

      return {
        name: `Attempt ${k}`,
        success: ofd ? ((delivered / ofd) * 100).toFixed(1) : 0,
      };
    });
  }, [cleanAttempts]);

  if (!data) return null;

  const delivered = data?.statusCounts?.DELIVERED || 0;
  const ofd = data?.statusCounts?.OUT_FOR_DELIVERY || 0;
  const failed =
    (data?.statusCounts?.RTO || 0) + (data?.statusCounts?.UNDELIVERED || 0);

    if(loading===true){
      return <SuwasthiLoader/>
    }

  return (
    <Layout>
      <Container overflow={"overflow-scroll"}>
        <div className="p-6 bg-gray-50 min-h-screen space-y-6">
          <div className="bg-white p-4 rounded-2xl shadow flex flex-wrap gap-4 items-end">
            <div>
              <label className="text-sm text-gray-500">Start Date</label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) =>
                  setFilters({ ...filters, startDate: e.target.value })
                }
                className="border p-2 rounded-lg"
              />
            </div>

            <div>
              <label className="text-sm text-gray-500">End Date</label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) =>
                  setFilters({ ...filters, endDate: e.target.value })
                }
                className="border p-2 rounded-lg"
              />
            </div>

            <button
              onClick={() => fetchData(filters.startDate, filters.endDate)}
              className="bg-blue-600 text-white px-5 py-2 rounded-lg hover:bg-blue-700"
            >
              Apply Filter
            </button>
          </div>

          {/* 🔥 KPI CARDS */}
          <div className="grid md:grid-cols-4 gap-4">
            <Card
              title="Delivered"
              value={delivered}
              color="green"
              icon={<CheckCircle />}
            />
            <Card
              title="Out For Delivery"
              value={ofd}
              color="blue"
              icon={<Truck />}
            />
            <Card
              title="Failed / RTO"
              value={failed}
              color="red"
              icon={<XCircle />}
            />
            <Card
              title="Total Shipments"
              value={data.totalShipments}
              color="gray"
              icon={<Package />}
            />
          </div>

          {/* 📊 GRAPH */}
          <div className="bg-white p-5 rounded-2xl shadow">
            <h2 className="font-semibold mb-4">Attempt Success Rate (%)</h2>

            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={graphData}>
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="success" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white p-5 rounded-2xl shadow">
            <h2 className="font-semibold mb-4">
              First Attempts (Date Based)
            </h2>

            <table className="w-full text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="p-2 text-left">Attempt</th>
                  <th className="p-2 text-center">Shipments</th>
                </tr>
              </thead>

              <tbody>
                <tr className="border-b">
                  <td className="p-2 font-medium">1st Attempt</td>
                  <td className="text-center text-blue-600 font-bold">
                    {data?.firstAttemptOFD || 0}
                  </td>
                </tr>

                <tr className="border-b">
                  <td className="p-2 font-medium">1st Attempt Delivered</td>
                  <td className="text-center text-purple-600 font-bold">
                    {data?.firstAttemptDelivered || 0}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* 📊 ATTEMPT TABLE */}
          <div className="bg-white p-5 rounded-2xl shadow">
            <h2 className="font-semibold mb-4">Attempt Analysis</h2>

            <table className="w-full text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="p-2 text-left">Attempt</th>
                  <th className="p-2">OFD</th>
                  <th className="p-2 text-green-600">Delivered</th>
                  <th className="p-2 text-red-600">Failed</th>
                  <th className="p-2">Success %</th>
                </tr>
              </thead>

              <tbody>
                {Object.entries(cleanAttempts).map(([k, v]) => {
                  const delivered = v?.DELIVERED || 0;
                  const ofd = v?.OUT_FOR_DELIVERY || 0;
                  const failed = (v?.RTO || 0) + (v?.UNDELIVERED || 0);

                  const percent = ofd
                    ? ((delivered / ofd) * 100).toFixed(1)
                    : 0;

                  return (
                    <tr key={k} className="border-b">
                      <td className="p-2 font-medium">Attempt {k}</td>
                      <td className="text-center">{ofd}</td>
                      <td className="text-center text-green-600">
                        {delivered}
                      </td>
                      <td className="text-center text-red-600">{failed}</td>
                      <td className="text-center font-semibold">{percent}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* 🚚 SOURCE PERFORMANCE */}
          <div className="bg-white p-5 rounded-2xl shadow">
            <h2 className="font-semibold mb-4">Carrier Performance</h2>

            <div className="grid md:grid-cols-2 gap-4">
              {Object.entries(data.sourceCounts).map(([source, val]) => (
                <div key={source} className="border p-4 rounded-xl bg-gray-50">
                  <h3 className="font-semibold mb-2 capitalize">{source}</h3>

                  <p className="text-sm text-gray-500 mb-2">
                    Total: {val.total}
                  </p>

                  <div className="space-y-1 text-sm">
                    {Object.entries(val.statusCounts).map(([s, c]) => (
                      <div key={s} className="flex justify-between">
                        <span>{s}</span>
                        <span className="font-medium">{c}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Container>
    </Layout>
  );
};

// 🎨 CARD
const Card = ({ title, value, icon, color }) => {
  const colors = {
    green: "bg-green-100 text-green-700",
    blue: "bg-blue-100 text-blue-700",
    red: "bg-red-100 text-red-700",
    gray: "bg-gray-100 text-gray-700",
  };

  return (
    <div className="bg-white p-4 rounded-2xl shadow flex justify-between items-center">
      <div>
        <p className="text-sm text-gray-500">{title}</p>
        <h2 className="text-xl font-bold">{value}</h2>
      </div>
      <div className={`p-3 rounded-full ${colors[color]}`}>{icon}</div>
    </div>
  );
};

export default ShipmentAnalytics;